// VS intro — shown for a few seconds after match_start before the
// real game UI becomes interactive. Mirrors Ludo's vs-screen pattern
// (gold sparks, banner shimmer, cards sliding in, V/S burst, POT chip
// fade-in) adapted for 2/4/6-player Rummy.
//
// The server's turn clock starts immediately on match_start, so the
// backend's FIRST_TURN_GRACE_SECONDS is bumped to cover this visual
// window — without the grace, the first active player would lose
// ~3 seconds of their turn while watching the intro.

type SeatProfile = {
  seat: string;
  displayName?: string;
  avatarUrl?: string | null;
  isBot?: boolean;
};

type Props = {
  mySeat: string | null;
  seats: SeatProfile[];
  pot: number;
  payout: number;
};

function fmt(n: number) {
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

// Random spark positions — fixed seed at render time; a 3-second
// overlay doesn't need dynamic positioning.
const SPARK_COUNT = 14;
const SPARKS = Array.from({ length: SPARK_COUNT }).map((_, i) => ({
  left:  `${(i * 7 + 5) % 95}%`,
  top:   `${85 + ((i * 13) % 10)}%`,
  delay: `${(i * 0.23) % 2.8}s`,
  scale: 0.6 + ((i * 0.17) % 0.8),
}));

export default function VsIntro({ mySeat, seats, pot, payout }: Props) {
  // Pick at most 3 cards to display (me + up to 2 opponents). In 4P /
  // 6P matches we show "me + first 2 opponents clockwise" — the same
  // rule Ludo's VS screen uses. The "+N more" chip signals additional
  // seats when there are more than 3 players total.
  const me = seats.find((s) => s.seat === mySeat) || seats[0];
  const others = seats.filter((s) => s.seat !== me?.seat).slice(0, 2);
  const extra  = Math.max(0, seats.length - (1 + others.length));

  return (
    <div id="rummy-vs-screen" className="overlay-screen themed z-[250]">
      <div className="rummy-vs-sparks" aria-hidden>
        {SPARKS.map((s, i) => (
          <span
            key={i}
            className="rummy-vs-spark"
            style={{
              left: s.left, top: s.top,
              animationDelay: s.delay,
              transform: `scale(${s.scale})`,
            }}
          />
        ))}
      </div>

      <div className="rummy-vs-banner">Match Found</div>

      <div className="rummy-vs-stage">
        {me && <VsCard profile={me} side="left-in" elevated />}
        <div className="rummy-vs-divider"><span>V/S</span></div>
        {others[0] && <VsCard profile={others[0]} side="right-in" />}
        {others[1] && <VsCard profile={others[1]} side="right-in" />}
        {extra > 0 && (
          <div className="rummy-vs-extra-chip">+{extra} more</div>
        )}
      </div>

      <div className="rummy-vs-pot">POT {fmt(pot)} &middot; WIN {fmt(payout)}</div>
    </div>
  );
}

function VsCard({
  profile, side, elevated,
}: { profile: SeatProfile; side: 'left-in' | 'right-in'; elevated?: boolean }) {
  const name = profile.displayName || profile.seat || 'Player';
  return (
    <div className={`rummy-vs-card ${side} ${elevated ? 'me' : ''}`}>
      {elevated && <div className="rummy-vs-you-badge">YOU</div>}
      <div className="rummy-vs-card-ring">
        {profile.avatarUrl
          ? <img src={profile.avatarUrl} alt="" />
          : (
            <div style={{
              width: '100%', height: '100%',
              background: '#22543d',
              display: 'grid', placeItems: 'center',
              color: '#fde08b', fontWeight: 900, fontSize: 26,
            }}>
              {name.slice(0, 1).toUpperCase()}
            </div>
          )}
      </div>
      <div className="rummy-vs-card-name">{name}</div>
      {/* "BOT" badge removed — players must never see that they're
          playing against a bot. The `profile.isBot` flag is still
          read for engine purposes but produces no visible output. */}
    </div>
  );
}
